import React, { useEffect, useState } from 'react'
import { Field, FormikProps } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'
import { Box, Button, CircularProgress, DialogActions, DialogContent, Typography } from '@mui/material'
import { UpdateQueryOptions } from '@apollo/client'
import { FormDatePicker, CustomTextField, GraphQLForm } from 'src/form'
import MeruDate from 'src/legacy/lib/MeruDate'
import { ClosableDialogTitle } from 'src/legacy/components/mui'
import { GET_PATIENT_HISTORY } from 'src/legacy/lib/graphql/queries'
import { trackRestartProgram } from 'src/mixpanel/api'
import {
  GetFullPatientDocument,
  GetPatientWithTagsDocument,
  useGetEarliestProgramStartDateQuery,
  useRestartParticipantProgramMutation,
} from '../patientQueries.generated'
import { getProgramStartDateSuggestion, isStartDateDisabled, programStartDateSchema } from './dateHelpers'

enum MenuOption {
  SELECT_START_DATE = 'SELECT_START_DATE',
  CONFIRM_RESTART = 'CONFIRM_RESTART',
}

type Props = {
  programStartDate: string
  uid: string
  close: () => void
  cancel: () => void
}

const SetRestartProgramSchema = (
  isAbTestWeekZeroRemovalVariant: boolean,
  earliestRestartDate: string | null | undefined
) =>
  Yup.object({
    uid: Yup.string().required(),
    programStartDate: programStartDateSchema(earliestRestartDate, isAbTestWeekZeroRemovalVariant),
    description: Yup.string().required(),
  }).required()

type RestartProgramForm = Yup.InferType<ReturnType<typeof SetRestartProgramSchema>>

type FormContentProps = {
  formik: FormikProps<RestartProgramForm>
  programStartDate: string
  earliestRestartDate: string | null | undefined
  isAbTestWeekZeroRemovalVariant: boolean
  close: () => void
  cancel: () => void
}

const SetRestartProgramFormContent = ({
  formik: { isSubmitting, values, errors, submitCount },
  programStartDate,
  earliestRestartDate,
  isAbTestWeekZeroRemovalVariant,
  close,
  cancel,
}: FormContentProps) => {
  const [renderSection, setRenderSection] = useState<MenuOption>(MenuOption.SELECT_START_DATE)

  // The date field lives on the first step; if its validation fails on
  // submit, bring the user back so the error is not on a hidden field.
  // This must update renderSection (not derive visibility from the error)
  // so that fixing the date does not auto-advance past the Next button
  // when validateOnChange clears the error.
  useEffect(() => {
    if (submitCount > 0 && errors.programStartDate) {
      setRenderSection(MenuOption.SELECT_START_DATE)
    }
  }, [submitCount, errors.programStartDate])

  const showStartDateSection = renderSection === MenuOption.SELECT_START_DATE

  return (
    <>
      <ClosableDialogTitle onClose={close}>Select new start date</ClosableDialogTitle>
      <DialogContent hidden={!showStartDateSection}>
        <Typography>Current start date: {moment(programStartDate).format('M/D/YYYY')}</Typography>
        <Field
          component={FormDatePicker}
          label="New start date"
          fullWidth
          name="programStartDate"
          shouldDisableDate={(date: moment.Moment) =>
            isStartDateDisabled(date, earliestRestartDate, isAbTestWeekZeroRemovalVariant)
          }
        />
      </DialogContent>
      <DialogContent hidden={showStartDateSection}>
        <Typography>
          You are about to restart this participant in the program. Their new start date for week 1 will be{' '}
          {moment(values.programStartDate).format('M/D/YYYY')}.
        </Typography>
        <Field
          component={CustomTextField}
          label="Reason for restart"
          name="description"
          multiline
          minRows={4}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        {showStartDateSection && (
          <>
            <Button color="inherit" onClick={cancel}>
              Cancel
            </Button>
            <Button color="primary" onClick={() => setRenderSection(MenuOption.CONFIRM_RESTART)}>
              Next
            </Button>
          </>
        )}
        {!showStartDateSection && (
          <>
            <Button color="inherit" onClick={() => setRenderSection(MenuOption.SELECT_START_DATE)}>
              Back
            </Button>
            <Button color="primary" disabled={isSubmitting} type="submit">
              Confirm
            </Button>
          </>
        )}
      </DialogActions>
    </>
  )
}

const SetRestartProgramForm = ({ programStartDate, uid, close, cancel }: Props) => {
  const [restartProgram] = useRestartParticipantProgramMutation({
    refetchQueries: [GET_PATIENT_HISTORY, GetFullPatientDocument, GetPatientWithTagsDocument],
    awaitRefetchQueries: true,
  })
  const { data: earliestProgramStartDateData, loading: earliestProgramStartDateLoading } =
    useGetEarliestProgramStartDateQuery({ variables: { uid } })

  const handleSubmit = async ({ variables }: UpdateQueryOptions<RestartProgramForm>) => {
    await restartProgram({ variables })
    trackRestartProgram(MeruDate.now().formatWeekNumber(programStartDate))
  }

  if (earliestProgramStartDateLoading) {
    return (
      <>
        <ClosableDialogTitle onClose={close}>Select new start date</ClosableDialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={cancel}>
            Cancel
          </Button>
        </DialogActions>
      </>
    )
  }

  const isAbTestWeekZeroRemovalVariant = earliestProgramStartDateData?.patient.isAbTestWeekZeroRemovalVariant ?? false
  const earliestStartDate = earliestProgramStartDateData?.patient.earliestProgramStartDate
  const earliestRestartDate = earliestProgramStartDateData?.patient.earliestRestartProgramStartDate
  const suggestedStartDate = getProgramStartDateSuggestion(earliestStartDate, isAbTestWeekZeroRemovalVariant).format(
    'YYYY-MM-DD'
  )

  return (
    <GraphQLForm
      initialValues={{ uid, programStartDate: suggestedStartDate, description: '' }}
      onSubmit={handleSubmit}
      onSuccess={close}
      validationSchema={SetRestartProgramSchema(isAbTestWeekZeroRemovalVariant, earliestRestartDate)}
      render={(formik) => (
        <SetRestartProgramFormContent
          formik={formik}
          programStartDate={programStartDate}
          earliestRestartDate={earliestRestartDate}
          isAbTestWeekZeroRemovalVariant={isAbTestWeekZeroRemovalVariant}
          close={close}
          cancel={cancel}
        />
      )}
    />
  )
}

export default SetRestartProgramForm
